import React, { useState, useCallback } from 'react';
import {
  MoreVertical, Zap, RotateCcw, Unplug, AlertCircle, Loader2,
  Clock, Eye, EyeOff, Pencil, X
} from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@hts/ui';
import type { IntegrationConnection, HealthStatus } from '../api/integrations.js';
import { getIntegrationMeta } from '../lib/integrationMeta.js';
import { useTestIntegration, useRotateSecret, useDisconnectIntegration } from '../hooks/useIntegrations.js';
import { useMenu } from '../hooks/useMenu.js';
import { ConnectWizard } from './ConnectWizard.js';

interface IntegrationCardProps {
  connection: IntegrationConnection;
}

function HealthBadge({ status }: { status: HealthStatus }) {
  const cfg: Record<HealthStatus, { label: string; cls: string; dot: string }> = {
    healthy: { label: 'Connected', cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', dot: 'bg-green-500' },
    degraded: { label: 'Degraded', cls: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400', dot: 'bg-yellow-500' },
    error: { label: 'Error', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', dot: 'bg-red-500' },
    unknown: { label: 'Unknown', cls: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400', dot: 'bg-gray-400' },
    connecting: { label: 'Connecting', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', dot: 'bg-blue-500' },
  };
  const { label, cls, dot } = cfg[status];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold ${cls}`} aria-label={`Status: ${label}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} aria-hidden />
      {label}
    </span>
  );
}

function RelativeTime({ iso }: { iso?: string }) {
  if (!iso) return <span className="text-xs text-gray-400">Never</span>;
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diff / 60_000);
  let label: string;
  if (mins < 1) label = 'just now';
  else if (mins < 60) label = `${mins}m ago`;
  else if (mins < 60 * 24) label = `${Math.round(mins / 60)}h ago`;
  else label = `${Math.round(mins / 60 / 24)}d ago`;
  return <span className="text-xs text-gray-500" title={new Date(iso).toLocaleString()}>{label}</span>;
}

function RotateDialog({
  open, onClose, onConfirm, isLoading
}: { open: boolean; onClose: () => void; onConfirm: (secret: string) => void; isLoading: boolean }) {
  const [secret, setSecret] = useState('');
  const [show, setShow] = useState(false);
  React.useEffect(() => { if (!open) { setSecret(''); setShow(false); } }, [open]);
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm p-6">
        <DialogTitle className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">Rotate Secret</DialogTitle>
        <DialogDescription className="text-sm text-gray-500 mb-4">
          Enter the new secret. The old one will be invalidated immediately.
        </DialogDescription>
        <div className="relative mb-4">
          <label htmlFor="rotate-secret" className="sr-only">New secret</label>
          <input
            id="rotate-secret"
            type={show ? 'text' : 'password'}
            value={secret}
            onChange={e => setSecret(e.target.value)}
            placeholder="New secret"
            className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 pr-10 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            autoComplete="new-password"
          />
          <button type="button" onClick={() => setShow(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" aria-label={show ? 'Hide secret' : 'Show secret'}>
            {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-3 py-2 rounded-md text-sm font-medium text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800">Cancel</button>
          <button
            type="button"
            onClick={() => { if (secret) onConfirm(secret); }}
            disabled={!secret || isLoading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
          >
            {isLoading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            Rotate
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DisconnectDialog({ open, name, onClose, onConfirm, isLoading }: {
  open: boolean; name: string; onClose: () => void; onConfirm: () => void; isLoading: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm p-6">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-red-100 dark:bg-red-900/30 mb-4" aria-hidden="true">
          <Unplug className="h-6 w-6 text-red-600" />
        </div>
        <DialogTitle className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">Disconnect {name}?</DialogTitle>
        <DialogDescription className="text-sm text-gray-500 mb-6">
          This will remove the stored credentials and stop all syncing. This cannot be undone.
        </DialogDescription>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-3 py-2 rounded-md text-sm font-medium text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800">Cancel</button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
          >
            {isLoading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            Disconnect
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function WizardModal({ kind, onClose, connection, editMode = false }: {
  kind: IntegrationConnection['kind'];
  connection?: IntegrationConnection;
  editMode?: boolean;
  onClose: () => void;
}) {
  const meta = getIntegrationMeta(kind);
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg p-0 overflow-hidden flex flex-col max-h-[90vh]">
        <div className="flex items-center gap-3 p-6 pb-4 border-b border-gray-200 dark:border-gray-800 shrink-0">
          <div className={`flex h-10 w-10 items-center justify-center rounded-xl text-xl ${meta.color}`} aria-hidden="true">
            {meta.icon}
          </div>
          <div>
            <DialogTitle className="text-base font-semibold text-gray-900 dark:text-gray-100">{editMode ? 'Edit' : 'Connect'} {meta.label}</DialogTitle>
            <DialogDescription className="text-xs text-gray-500">{meta.description}</DialogDescription>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          <ConnectWizard kind={kind} existingConnection={connection} editMode={editMode} onClose={onClose} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Compact list of the saved config values (non-secret) for the card body. */
function ConfigSummary({ kind, config }: { kind: IntegrationConnection['kind']; config: Record<string, string> }) {
  const meta = getIntegrationMeta(kind);
  const entries = meta.configFields
    .map((f) => [f.label, config[f.key]] as const)
    .filter(([, v]) => v !== undefined && v !== '');
  if (entries.length === 0) return null;
  return (
    <dl className="px-5 pb-3 space-y-1" aria-label="Configuration">
      {entries.map(([label, value]) => (
        <div key={label} className="flex items-baseline justify-between gap-3 text-xs">
          <dt className="text-gray-400 shrink-0">{label}</dt>
          <dd className="text-gray-600 dark:text-gray-300 font-mono truncate" title={value}>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function IntegrationCard({ connection }: IntegrationCardProps) {
  const meta = getIntegrationMeta(connection.kind);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showRotate, setShowRotate] = useState(false);
  const [showDisconnect, setShowDisconnect] = useState(false);

  const closeMenu = useCallback(() => setMenuOpen(false), []);
  const menu = useMenu(menuOpen, closeMenu);

  const testMutation = useTestIntegration();
  const rotateMutation = useRotateSecret();
  const disconnectMutation = useDisconnectIntegration();

  const isConnected = connection.status === 'connected';
  const isDisconnected = connection.status === 'disconnected';
  // Saved but not healthy (error / pending): offer fix-up actions too.
  const isConfiguredNotConnected = !isConnected && !isDisconnected;
  const isTesting = testMutation.isPending && testMutation.variables === connection.id;

  function handleTest() {
    setMenuOpen(false);
    testMutation.mutate(connection.id);
  }

  function handleRotate(secret: string) {
    rotateMutation.mutate({ id: connection.id, input: { secret } }, {
      onSuccess: () => setShowRotate(false),
    });
  }

  function handleDisconnect() {
    disconnectMutation.mutate(connection.id, {
      onSuccess: () => setShowDisconnect(false),
    });
  }

  return (
    <>
      <div className={`
        group relative flex flex-col rounded-2xl border bg-white dark:bg-gray-900 shadow-sm
        transition-all duration-200 hover:shadow-md
        ${isDisconnected ? 'border-dashed border-gray-300 dark:border-gray-700 opacity-75' : 'border-gray-200 dark:border-gray-800'}
        ${connection.health === 'error' ? 'border-red-200 dark:border-red-800' : ''}
      `}>
        {/* Card header */}
        <div className="flex items-start justify-between p-5 pb-4">
          <div className="flex items-center gap-3">
            <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-2xl ${meta.color}`} aria-hidden="true">
              {meta.icon}
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                {connection.displayName}
              </h3>
              <p className="text-xs text-gray-500 truncate">{meta.summary}</p>
            </div>
          </div>

          {/* Actions menu */}
          <div className="relative shrink-0">
            <button
              ref={menu.triggerRef}
              type="button"
              onClick={() => setMenuOpen(v => !v)}
              className="flex h-8 w-8 items-center justify-center rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label={`Actions for ${connection.displayName}`}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
            >
              <MoreVertical className="h-4 w-4" aria-hidden="true" />
            </button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-20" onClick={closeMenu} aria-hidden="true" />
                <div
                  ref={menu.menuRef}
                  role="menu"
                  aria-label={`${connection.displayName} actions`}
                  className="absolute right-0 top-9 z-30 w-44 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg py-1"
                >
                  {isConnected && (
                    <>
                      <MenuButton icon={<Pencil className="h-3.5 w-3.5" aria-hidden="true" />} onClick={() => { setMenuOpen(false); setShowEdit(true); }}>
                        Edit configuration
                      </MenuButton>
                      <MenuButton icon={<Zap className="h-3.5 w-3.5" aria-hidden="true" />} onClick={handleTest} disabled={isTesting}>
                        {isTesting ? 'Testing…' : 'Test connection'}
                      </MenuButton>
                      <MenuButton icon={<RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />} onClick={() => { setMenuOpen(false); setShowRotate(true); }}>
                        Rotate secret
                      </MenuButton>
                      <div className="my-1 h-px bg-gray-200 dark:bg-gray-700" aria-hidden="true" />
                      <MenuButton icon={<Unplug className="h-3.5 w-3.5" aria-hidden="true" />} onClick={() => { setMenuOpen(false); setShowDisconnect(true); }} danger>
                        Disconnect
                      </MenuButton>
                    </>
                  )}
                  {isConfiguredNotConnected && (
                    <>
                      <MenuButton icon={<Pencil className="h-3.5 w-3.5" aria-hidden="true" />} onClick={() => { setMenuOpen(false); setShowEdit(true); }}>
                        Fix configuration
                      </MenuButton>
                      <MenuButton icon={<Zap className="h-3.5 w-3.5" aria-hidden="true" />} onClick={handleTest} disabled={isTesting}>
                        {isTesting ? 'Testing…' : 'Retry test'}
                      </MenuButton>
                      <div className="my-1 h-px bg-gray-200 dark:bg-gray-700" aria-hidden="true" />
                      <MenuButton icon={<Unplug className="h-3.5 w-3.5" aria-hidden="true" />} onClick={() => { setMenuOpen(false); setShowDisconnect(true); }} danger>
                        Disconnect
                      </MenuButton>
                    </>
                  )}
                  {isDisconnected && (
                    <MenuButton icon={<Zap className="h-3.5 w-3.5" aria-hidden="true" />} onClick={() => { setMenuOpen(false); setShowWizard(true); }}>
                      Connect
                    </MenuButton>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Status row */}
        <div className="flex items-center justify-between px-5 py-2 border-t border-gray-100 dark:border-gray-800">
          <HealthBadge status={connection.health} />
          {(connection.lastSyncAt || connection.lastTestedAt) && (
            <div className="flex items-center gap-1 text-gray-400" title={connection.lastSyncAt ? 'Last successful sync' : 'Last tested'}>
              <Clock className="h-3 w-3" aria-hidden="true" />
              <span className="text-xs text-gray-400">{connection.lastSyncAt ? 'Synced' : 'Tested'}</span>
              <RelativeTime iso={connection.lastSyncAt ?? connection.lastTestedAt} />
            </div>
          )}
        </div>

        {/* Error message */}
        {connection.lastError && connection.health === 'error' && (
          <div className="mx-4 mb-4 mt-2 flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-900/20 p-2.5 text-xs text-red-700 dark:text-red-400">
            <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" aria-hidden="true" />
            <span className="line-clamp-2">{connection.lastError}</span>
          </div>
        )}

        {/* Saved config values (non-secret) */}
        {isConnected && <ConfigSummary kind={connection.kind} config={connection.config} />}

        {/* Secret hint: proves a secret is stored without revealing it */}
        {isConnected && (
          <div className="px-5 pb-4 text-xs text-gray-400 font-mono">
            {connection.secretHint ? `Key: ${connection.secretHint}` : 'Key: •••• (saved)'}
          </div>
        )}

        {/* Fix CTA for saved-but-unhealthy connections */}
        {isConfiguredNotConnected && (
          <div className="px-5 pb-5 pt-2">
            <button
              type="button"
              onClick={() => setShowEdit(true)}
              className="w-full rounded-lg border border-red-300 dark:border-red-800 py-2.5 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            >
              Fix configuration →
            </button>
          </div>
        )}

        {/* Connect CTA for disconnected */}
        {isDisconnected && (
          <div className="px-5 pb-5 pt-2">
            <button
              type="button"
              onClick={() => setShowWizard(true)}
              className="w-full rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700 py-2.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:border-blue-400 hover:text-blue-600 transition-colors"
            >
              Connect →
            </button>
          </div>
        )}

        {/* Test loading overlay */}
        {isTesting && (
          <div className="absolute inset-0 flex items-center justify-center rounded-2xl bg-white/70 dark:bg-gray-900/70 backdrop-blur-[1px]" role="status">
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Testing…
            </div>
          </div>
        )}
      </div>

      {/* Dialogs */}
      {showWizard && (
        <WizardModal
          kind={connection.kind}
          connection={isConnected ? connection : undefined}
          onClose={() => setShowWizard(false)}
        />
      )}
      {showEdit && !isDisconnected && (
        <WizardModal
          kind={connection.kind}
          connection={connection}
          editMode
          onClose={() => setShowEdit(false)}
        />
      )}
      <RotateDialog
        open={showRotate}
        onClose={() => setShowRotate(false)}
        onConfirm={handleRotate}
        isLoading={rotateMutation.isPending}
      />
      <DisconnectDialog
        open={showDisconnect}
        name={connection.displayName}
        onClose={() => setShowDisconnect(false)}
        onConfirm={handleDisconnect}
        isLoading={disconnectMutation.isPending}
      />
    </>
  );
}

function MenuButton({
  icon, children, onClick, danger, disabled,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      disabled={disabled}
      className={`flex w-full items-center gap-2 px-3 py-2 text-xs font-medium transition-colors disabled:opacity-50 ${
        danger
          ? 'text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20'
          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
      }`}
    >
      {icon}
      {children}
    </button>
  );
}
